import { Injectable, inject } from '@angular/core';
import { Observable, take } from 'rxjs';

import { I18nService } from '@ts/shared/i18n/util-core';

import { BormadagoTitleStrategy } from './title.service';

@Injectable({
  providedIn: 'root',
})
export class TitleResolverService {
  private titleService = inject(BormadagoTitleStrategy);
  private i18nService = inject(I18nService);

  /**
   * Sets the page title from the first emission of a stream. `take(1)` completes
   * the subscription, so no manual cleanup is needed.
   *
   * @example
   * titleResolver.setTitleFromStream(facility$, (facility) => facility.name);
   */
  setTitleFromStream<T>(
    stream$: Observable<T>,
    resolver: (entity: T) => string,
  ): void {
    stream$.pipe(take(1)).subscribe((entity) => {
      this.titleService.setTitle(resolver(entity));
    });
  }

  /**
   * Like {@link setTitleFromStream}, but resolves the title through a translation
   * key with params.
   *
   * @example
   * titleResolver.setTitleFromStreamTranslated(
   *   order$, 'order.orderWithNumber', (order) => ({ orderNumber: order.number }),
   * );
   */
  setTitleFromStreamTranslated<T>(
    stream$: Observable<T>,
    translationKey: string,
    paramsResolver: (entity: T) => Record<string, string | number>,
  ): void {
    stream$.pipe(take(1)).subscribe((entity) => {
      this.i18nService
        .translate$(translationKey, paramsResolver(entity))
        .then((translation) => {
          this.titleService.setTitle(translation);
        });
    });
  }
}
